/**
 * QUIPWA — toasts
 *
 * Transient, non-blocking feedback ("Sincronizado con Drive", "No se pudo
 * subir la imagen"). Replaces `window.alert`, which blocked the page, could
 * not be styled and ignored the app's language.
 *
 * ```ts
 * import { toast } from '@/components/system/toast-store'
 * toast.success(t('sync.toast.done'))
 * toast.error(t('appearance.uploadFailed'), { action: { label: t('common.retry'), run: retry } })
 * ```
 */
import { create } from 'zustand'

export type ToastTone = 'success' | 'error' | 'info'

export interface ToastAction {
  label: string
  run: () => void
}

export interface Toast {
  id: number
  tone: ToastTone
  message: string
  action?: ToastAction
  /** ms; 0 keeps it until dismissed. */
  duration: number
}

interface ToastState {
  toasts: Toast[]
  push: (toast: Omit<Toast, 'id'>) => number
  dismiss: (id: number) => void
}

const MAX_VISIBLE = 3
let nextId = 1

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (toast) => {
    const id = nextId++
    set((s) => ({ toasts: [...s.toasts, { ...toast, id }].slice(-MAX_VISIBLE) }))
    return id
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}))

interface ToastOptions {
  action?: ToastAction
  duration?: number
}

function show(tone: ToastTone, message: string, options: ToastOptions = {}): number {
  return useToasts.getState().push({
    tone,
    message,
    duration: options.duration ?? (tone === 'error' ? 8000 : 4500),
    ...(options.action ? { action: options.action } : {}),
  })
}

export const toast = {
  success: (message: string, options?: ToastOptions) => show('success', message, options),
  error: (message: string, options?: ToastOptions) => show('error', message, options),
  info: (message: string, options?: ToastOptions) => show('info', message, options),
  dismiss: (id: number) => useToasts.getState().dismiss(id),
}
